// workbook.tsx — Outform's own estimating spreadsheets, on the board, live.
//
// WHY (Erez, 18 Aug 2026): "I want you to put them on the board. Is it possible
// to show them as Excel files on the board, that I can actually look at them and
// touch them?"
//
// ── WHY THIS IS A GRID AND NOT A SCREENSHOT ──────────────────────────────────
// A picture of a spreadsheet shows the numbers, and the numbers are the least
// interesting thing in these files. What matters is underneath:
//
//   · 41,140 formulas, of which 3,516 evaluate to an ERROR
//   · every cell that would tell an estimator the margin on the job is #REF!
//   · 25 of the 26 sheets are HIDDEN, and the one visible sheet is clean
//
// None of that survives a screenshot. So the workbook is stored as data
// (sql/055, /api/xl-sheets) and drawn as a grid: click any cell and its formula
// appears in the bar, errors are painted red, and the hidden sheets are on the
// tab strip with the rest — which is the whole point, because the estimator
// cannot see them and we can.
//
// ⚠ THE ROW CAP IS ANNOUNCED, NEVER SILENT. 'Work' is 1,001 rows × 69 columns;
// rendering it whole is ~69,000 DOM nodes and the board is already carrying a
// PDF marker. So the grid draws the first ROW_CAP rows and SAYS SO on the face
// of it, with a button to draw the rest. A truncation you cannot see reads as
// "that is the whole file", which is exactly the kind of quiet wrongness this
// section exists to expose.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "@/lib/api";

const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

const T = {
  paper: "#FFFFFF",
  ink: "#1C1917",
  muted: "#78716C",
  rule: "#E2E0DC",
  headBg: "#F3F2EF",
  chrome: "#26433C",      // the frame's own dark band
  tabOn: "#1F5F4B",
  formula: "#9A5B00",     // amber — a cell that is computed
  formulaBg: "#FEF9F0",
  error: "#B91C1C",
  errorBg: "#FEF0F0",
  hidden: "#A8A29E",
};

const ROW_CAP = 220;

type SheetMeta = {
  workbook_slug: string; name: string; ord: number; hidden: boolean;
  n_rows: number; n_cols: number; n_cells: number; n_errors: number;
};
type Book = { slug: string; filename: string; label: string; note: string | null; sheets: SheetMeta[] };
/** [row, col, display, formula|null, kind] — see sql/055. */
type Cell = [number, number, string, string | null, string];
type Sheet = SheetMeta & { cells: Cell[] };

/** 1 → A, 27 → AA. Excel's column names, because a cell reference the reader
 *  can type back into Excel is the only reference worth showing. */
function colName(n: number): string {
  let s = "";
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
  return s;
}

export function OutformWorkbooks({ width = 2600, height = 1500 }: { width?: number; height?: number }) {
  const api = useApi();
  const [books, setBooks] = useState<Book[]>([]);
  const [bookSlug, setBookSlug] = useState<string | null>(null);
  const [sheetName, setSheetName] = useState<string | null>(null);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [sel, setSel] = useState<{ r: number; c: number } | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await api("/api/xl-sheets");
        if (!res.ok) throw new Error(`index ${res.status}`);
        const data = (await res.json()) as Book[];
        if (!live) return;
        setBooks(data);
        if (data.length) { setBookSlug(data[0].slug); setSheetName(data[0].sheets[1]?.name ?? data[0].sheets[0]?.name ?? null); }
      } catch (e) { if (live) setErr(String(e)); }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!bookSlug || !sheetName) return;
    let live = true;
    setSheet(null); setSel(null); setShowAll(false);
    (async () => {
      try {
        const res = await api(`/api/xl-sheets?workbook=${encodeURIComponent(bookSlug)}&sheet=${encodeURIComponent(sheetName)}`);
        if (!res.ok) throw new Error(`sheet ${res.status}`);
        const data = (await res.json()) as Sheet;
        if (live) setSheet(data);
      } catch (e) { if (live) setErr(String(e)); }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookSlug, sheetName]);

  const book = books.find((b) => b.slug === bookSlug) ?? null;

  const grid = useMemo(() => {
    if (!sheet) return null;
    const map = new Map<string, Cell>();
    for (const c of sheet.cells) map.set(`${c[0]}:${c[1]}`, c);
    const rows = showAll ? sheet.n_rows : Math.min(sheet.n_rows, ROW_CAP);
    const cols = Math.min(sheet.n_cols, 90);
    return { map, rows, cols, truncated: sheet.n_rows > rows };
  }, [sheet, showAll]);

  const selCell = useMemo(() => {
    if (!grid || !sel) return null;
    return grid.map.get(`${sel.r}:${sel.c}`) ?? null;
  }, [grid, sel]);

  const pick = useCallback((r: number, c: number) => setSel({ r, c }), []);

  return (
    <div style={{ width, fontFamily: FONT, color: T.ink, background: T.paper,
                  border: `1px solid ${T.rule}`, borderRadius: 14, overflow: "hidden",
                  boxShadow: "0 2px 10px rgba(0,0,0,.06)", display: "flex", flexDirection: "column", height }}>

      {/* ── the three files ─────────────────────────────────────────────── */}
      <div style={{ background: T.chrome, color: "#F5F4F1", padding: "14px 20px 0", flexShrink: 0 }}>
        <div style={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", opacity: .68, marginBottom: 10 }}>
          Sent by Zahid Randhawa, Head of Estimating · 18 August 2026 · job 261152 · L'Oréal / Ulta Prada Fragrance Lit Glorifier · 297 units
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {books.map((b) => {
            const on = b.slug === bookSlug;
            return (
              <button key={b.slug}
                onClick={() => { setBookSlug(b.slug); setSheetName(b.sheets[1]?.name ?? b.sheets[0]?.name ?? null); }}
                style={{ border: "none", cursor: "pointer", fontFamily: FONT, fontSize: 14, fontWeight: on ? 650 : 500,
                         padding: "10px 18px", borderRadius: "10px 10px 0 0",
                         background: on ? T.paper : "rgba(255,255,255,.10)", color: on ? T.ink : "#DCD9D3" }}>
                {b.label.split(" — ")[0]}
                <span style={{ fontSize: 11, opacity: .6, marginLeft: 8, fontFamily: MONO }}>
                  {b.sheets.length} sheet{b.sheets.length === 1 ? "" : "s"}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {book && (
        <div style={{ padding: "10px 20px", borderBottom: `1px solid ${T.rule}`, background: "#FAFAF8", flexShrink: 0 }}>
          <div style={{ fontFamily: MONO, fontSize: 12, color: T.muted }}>{book.filename}</div>
          {book.note && <div style={{ fontSize: 13, marginTop: 4, color: T.ink }}>{book.note}</div>}
        </div>
      )}

      {/* ── formula bar ─────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "stretch", borderBottom: `1px solid ${T.rule}`, flexShrink: 0 }}>
        <div style={{ width: 92, padding: "8px 12px", borderRight: `1px solid ${T.rule}`, fontFamily: MONO,
                      fontSize: 13, background: T.headBg, display: "flex", alignItems: "center" }}>
          {sel ? `${colName(sel.c)}${sel.r}` : "—"}
        </div>
        <div style={{ flex: 1, padding: "8px 14px", fontFamily: MONO, fontSize: 13, display: "flex",
                      alignItems: "center", gap: 12, minWidth: 0,
                      color: selCell?.[3] ? T.formula : T.ink }}>
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {selCell ? (selCell[3] ?? selCell[2]) : "click any cell to read its formula"}
          </span>
          {selCell?.[3] && (
            <span style={{ marginLeft: "auto", flexShrink: 0, color: selCell[4] === "e" ? T.error : T.muted }}>
              = {selCell[2]}
            </span>
          )}
        </div>
      </div>

      {/* ── the grid ────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: "auto", background: T.paper, minHeight: 0 }}>
        {err && <div style={{ padding: 24, color: T.error, fontFamily: MONO, fontSize: 13 }}>{err}</div>}
        {!sheet && !err && <div style={{ padding: 24, color: T.muted, fontSize: 14 }}>Loading sheet…</div>}
        {grid && sheet && (
          <table style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 12.5, tableLayout: "fixed" }}>
            <thead>
              <tr>
                <th style={{ position: "sticky", top: 0, left: 0, zIndex: 3, width: 52, minWidth: 52,
                             background: T.headBg, borderRight: `1px solid ${T.rule}`, borderBottom: `1px solid ${T.rule}` }} />
                {Array.from({ length: grid.cols }, (_, i) => (
                  <th key={i} style={{ position: "sticky", top: 0, zIndex: 2, width: 116, minWidth: 116,
                                       background: T.headBg, borderRight: `1px solid ${T.rule}`, borderBottom: `1px solid ${T.rule}`,
                                       fontFamily: MONO, fontSize: 11, fontWeight: 500, color: T.muted, padding: "5px 0" }}>
                    {colName(i + 1)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: grid.rows }, (_, ri) => {
                const r = ri + 1;
                return (
                  <tr key={r}>
                    <th style={{ position: "sticky", left: 0, zIndex: 1, background: T.headBg, fontFamily: MONO,
                                 fontSize: 11, fontWeight: 500, color: T.muted, borderRight: `1px solid ${T.rule}`,
                                 borderBottom: `1px solid ${T.rule}`, padding: "3px 6px", textAlign: "right" }}>
                      {r}
                    </th>
                    {Array.from({ length: grid.cols }, (_, ci) => {
                      const c = ci + 1;
                      const cell = grid.map.get(`${r}:${c}`);
                      const isErr = cell?.[4] === "e";
                      const isFx = !!cell?.[3];
                      const on = sel?.r === r && sel?.c === c;
                      return (
                        <td key={c} onClick={() => pick(r, c)}
                            title={cell?.[3] ?? undefined}
                            style={{ borderRight: `1px solid ${T.rule}`, borderBottom: `1px solid ${T.rule}`,
                                     padding: "3px 7px", cursor: "pointer", whiteSpace: "nowrap",
                                     overflow: "hidden", textOverflow: "ellipsis",
                                     textAlign: cell?.[4] === "n" ? "right" : "left",
                                     fontFamily: cell?.[4] === "n" || isErr ? MONO : FONT,
                                     color: isErr ? T.error : T.ink,
                                     fontWeight: isErr ? 600 : 400,
                                     background: on ? "#DBEAFE" : isErr ? T.errorBg : isFx ? T.formulaBg : "transparent",
                                     boxShadow: on ? `inset 0 0 0 2px ${T.tabOn}` : undefined }}>
                          {cell?.[2] ?? ""}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {grid?.truncated && (
          <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.rule}`, display: "flex", alignItems: "center", gap: 16 }}>
            <span style={{ fontSize: 13, color: T.muted }}>
              Showing rows 1–{grid.rows} of {sheet?.n_rows.toLocaleString()}.
            </span>
            <button onClick={() => setShowAll(true)}
                    style={{ border: `1px solid ${T.rule}`, background: T.paper, borderRadius: 8, padding: "6px 14px",
                             fontFamily: FONT, fontSize: 13, cursor: "pointer" }}>
              Draw the rest
            </button>
          </div>
        )}
      </div>

      {/* ── sheet tabs, hidden ones included and marked ─────────────────── */}
      <div style={{ borderTop: `1px solid ${T.rule}`, background: "#FAFAF8", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 3, padding: "8px 12px 0", overflowX: "auto" }}>
          {book?.sheets.map((s) => {
            const on = s.name === sheetName;
            return (
              <button key={s.name} onClick={() => setSheetName(s.name)}
                style={{ border: "none", cursor: "pointer", fontFamily: FONT, fontSize: 12.5, whiteSpace: "nowrap",
                         padding: "7px 13px", borderRadius: "8px 8px 0 0", flexShrink: 0,
                         fontWeight: on ? 650 : 450,
                         background: on ? T.tabOn : "transparent",
                         color: on ? "#FFFFFF" : s.hidden ? T.hidden : T.ink,
                         fontStyle: s.hidden ? "italic" : "normal" }}>
                {s.hidden && <span style={{ marginRight: 6, opacity: .8 }}>◌</span>}
                {s.name}
                {s.n_errors > 0 && (
                  <span style={{ marginLeft: 7, fontFamily: MONO, fontSize: 10.5, fontWeight: 700,
                                 color: on ? "#FCA5A5" : T.error }}>
                    {s.n_errors}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 26, padding: "9px 16px", borderTop: `1px solid ${T.rule}`,
                      fontSize: 11.5, color: T.muted, alignItems: "center" }}>
          <span><span style={{ color: T.hidden }}>◌</span> hidden from the estimator</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: T.formulaBg,
                               border: `1px solid ${T.rule}`, verticalAlign: -1, marginRight: 5 }} /> a formula</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: T.errorBg,
                               border: `1px solid ${T.error}`, verticalAlign: -1, marginRight: 5 }} /> evaluates to an error</span>
          {sheet && (
            <span style={{ marginLeft: "auto", fontFamily: MONO }}>
              {sheet.name} · {sheet.n_rows.toLocaleString()}×{sheet.n_cols} · {sheet.n_cells.toLocaleString()} cells ·{" "}
              <b style={{ color: sheet.n_errors ? T.error : T.muted }}>{sheet.n_errors.toLocaleString()} errors</b>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
